import { useCallback, useEffect, useState } from 'react';
import { TRIGGERS, type Trigger } from '@shared/constants';
import { Button } from '@/components/Button';
import { FlexColPageContainer } from '@/components/FlexColPageContainer';
import { Modal } from '@/components/Modal';
import { ScrollShadow } from '@/components/ScrollShadow';
import { XIcon } from '@/components/icons/XIcon';
import { navigate } from '@/features/routing';
import { getDebugRing } from '@/lib/devUtils';
import { useMoodRecordDraft } from '@/lib/moodUtils/MoodRecordDraftContext';
import { clx } from '@/lib/utils';
import { ContinueButton } from '../ContinueButton';
import { Heading } from '../Heading';

export const ContextForm = () => {
  const { moodRecordDraft, updateMoodRecordDraft } = useMoodRecordDraft();

  const [showModal, setShowModal] = useState(false);
  const [shouldShowCTA, setShouldShowCTA] = useState(
    (moodRecordDraft?.triggers?.length || 0) === 2
  );
  const [triggers, setTriggers] = useState(moodRecordDraft?.triggers || []);

  const [majorContext, _setMajorContext] = useState<string>('');

  const toggleTrigger = useCallback(
    (trigger: Trigger) => {
      if (triggers.includes(trigger)) {
        setTriggers((prev) => prev.filter((t) => t !== trigger));
      } else {
        setTriggers((prev) => [...prev, trigger]);
      }
      setShouldShowCTA(triggers.length === 2);
    },
    [triggers, setTriggers, setShouldShowCTA]
  );

  const setMajorContext = useCallback(
    (value: string) => {
      _setMajorContext(value);
      setShowModal(true);
    },
    [_setMajorContext, setShowModal]
  );

  useEffect(() => {
    if (!shouldShowCTA) {
      setShouldShowCTA(triggers.length == 2);
    }
  }, [shouldShowCTA, triggers]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (triggers.length == 2) {
        const success = await updateMoodRecordDraft({ triggers });
        if (success) {
          navigate('/mood-log-entry/step4');
        } else {
          // TODO: Handle this error. Could add toast notification here.
        }
      } else {
        // TODO: Handle this other error case. Could add toast notification here.
      }
    },
    [triggers, updateMoodRecordDraft]
  );

  if (!moodRecordDraft?.mood) {
    return (
      <FlexColPageContainer className="justify-start pt-3">
        <Heading className="mb-6">What makes you feel that way?</Heading>
        <div className="mx-auto my-3 w-10/12 text-center">
          <h1 className="text-2xl text-black">Please select your mood first.</h1>
          <Button onClick={() => history.go(-2)} variant="buttonPrimary">
            Go to Step 1
          </Button>
        </div>
      </FlexColPageContainer>
    );
  }

  return (
    <FlexColPageContainer className="justify-start pt-3">
      <Heading className="mb-6">What makes you feel that way?</Heading>

      <form
        onSubmit={handleSubmit}
        className={clx('w-xs flex min-h-48 shrink flex-col', getDebugRing('blue'))}
      >
        <ScrollShadow
          innerClassName={clx(
            'mx-auto grid min-h-36 w-full shrink grid-cols-1 gap-4 pt-3'
            // getDebugRing('red')
          )}
        >
          {Object.values(TRIGGERS).map((majorContext) => (
            <Button
              className="w-full drop-shadow"
              key={majorContext}
              name="majorContext"
              onClick={() => setMajorContext(majorContext)}
              type="button"
              variant="buttonPrimary"
            >
              {majorContext}
            </Button>
          ))}
        </ScrollShadow>

        {triggers.length > 0 && (
          <div className="mt-7 flex justify-around">
            {triggers.map((trigger) => (
              <div
                key={trigger}
                className="bg-background-secondary flex h-9 items-center gap-3 rounded-full border pl-2 pr-2.5"
              >
                {trigger} <XIcon className="size-2.5" />
              </div>
            ))}
          </div>
        )}

        {/* The continue button. */}
        <ContinueButton
          className={clx(
            'transition-all duration-100',
            shouldShowCTA ? '' : 'h-0 overflow-hidden p-0'
          )}
          disabled={!shouldShowCTA || triggers.length !== 2}
        />

        {/* Trigger Picker modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={majorContext}
          containerClassName="bg-background-primary w-11/12 basis-3/5"
        >
          <div className="my-8 grid grid-cols-2 gap-4">
            {Object.values(TRIGGERS).map((trigger) => (
              <Button
                className="w-full"
                key={trigger}
                name="trigger"
                onClick={() => toggleTrigger(trigger)}
                type="button"
                variant="buttonPrimary"
              >
                {trigger}
              </Button>
            ))}
          </div>
        </Modal>
      </form>
    </FlexColPageContainer>
  );
};
